// eslint-disable-next-line no-restricted-imports
import {ssrSafeHistory, ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'

export type HistoryState = {
  // React Router's id for a route
  key?: string
  // ID of the current `<react-app>`, it can either be an uuid or `rails` if there is no app present. Used
  // to decide if Turbo should restore a page or not during back/forward navigation.
  appId?: string
  // DEPRECATED
  skipTurbo?: boolean
  // Used by `updatable-content`
  staleRecords?: {[key: string]: string}
  // Managed by Turbo, the restorationIdentifier is used to restore a page during back/forward navigation
  turbo?: {
    restorationIdentifier: string
  }
  // How many Turbo navigations happened in this session, used by the staffbar soft navigation label
  turboCount?: number
  // DEPRECATED
  usr?: {
    skipTurbo?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __prefetched_data?: any
  }
  // Used by React navigations to decide if we need to restore Turbo elements
  restoreTurboElements?: {appName: string; idsToRestore: string[]}
  restoreReactElements?: {appName: string; idsToRemove: string[]}
}
let lastState: HistoryState | null = null

export function currentState(): HistoryState {
  return ssrSafeHistory?.state || {}
}

export function historyLength(): number {
  return ssrSafeHistory?.length ?? 0
}

export function updateUrl(newUrl: string) {
  replaceStateWithEvent(currentState(), '', newUrl)
}

export function addUrlToHistoryStack(newUrl: string) {
  ssrSafeHistory?.pushState({appId: currentState().appId}, '', newUrl)
  dispatchStateChange()
}

export function updateCurrentState(state: HistoryState) {
  const mergedState = {
    ...currentState(),
    ...state,
  }
  if (lastState) lastState = mergedState
  replaceStateWithEvent(mergedState, '', location.href)
}

export function updateSearchParams(searchParams: URLSearchParams) {
  updateUrl(`?${searchParams.toString()}${ssrSafeLocation.hash}`)
}

export function removeSearchParams() {
  updateUrl(ssrSafeLocation.pathname + ssrSafeLocation.hash)
}

export function updateUrlHash(hash: string) {
  const hashWithPrefix = hash.startsWith('#') ? hash : `#${hash}`
  updateUrl(hashWithPrefix)
  lastState = currentState()

  // No state to restore
  if (!lastState) return
  // Firefox has a bug where it resets the state to null when updating the hash. This reset
  // happens asynchronously after the hash update, so we need to wait for the browser to be idle
  // to restore the state.
  requestIdleCallback(() => {
    // eslint-disable-next-line no-restricted-globals
    if (!history.state && lastState) {
      updateCurrentState(lastState)
      lastState = null
    }
  })
}

export function removeUrlHash() {
  updateUrl(ssrSafeLocation.pathname + ssrSafeLocation.search)
}

export function goBack() {
  ssrSafeHistory?.back()
}

export function goForward() {
  ssrSafeHistory?.forward()
}

function replaceStateWithEvent(data: HistoryState, unused: string, url: string | URL) {
  ssrSafeHistory?.replaceState(data, unused, url)
  dispatchStateChange()
}

function dispatchStateChange() {
  ssrSafeWindow?.dispatchEvent(new CustomEvent('statechange', {bubbles: false, cancelable: false}))
}
